import React, { useState, useEffect } from 'react';
import { getProfile, updateProfile, generateId, uploadImage } from '../../utils/api';

const newHistoryItem = () => ({
    id: generateId(),
    year: '',
    icon: '',
    title: '',
    subtitle: '',
});

// 최근 연도 먼저, 같은 연도 내에서는 등록(입력) 순서를 유지 (stable sort)
const sortHistory = (history) => {
    const yearValue = (y) => {
        const n = parseInt(y, 10);
        return Number.isNaN(n) ? Infinity : n;
    };
    return [...history].sort((a, b) => yearValue(b.year) - yearValue(a.year));
};

function AdminProfileEdit() {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const [ok, setOk] = useState('');
    const [uploadingIds, setUploadingIds] = useState({});

    useEffect(() => {
        getProfile()
            .then(data => setProfile({ summary: data.summary || '', history: sortHistory(data.history || []) }))
            .catch(e => {
                setErr(e.message);
                setProfile({ summary: '', history: [] });
            })
            .finally(() => setLoading(false));
    }, []);

    const handleImageUpload = async (file, uploadId, onSuccess) => {
        if (!file) return;
        setUploadingIds(prev => ({ ...prev, [uploadId]: true }));
        try {
            const { url } = await uploadImage(file);
            onSuccess(url);
        } catch (e) {
            setErr('이미지 업로드 실패: ' + e.message);
        } finally {
            setUploadingIds(prev => { const n = { ...prev }; delete n[uploadId]; return n; });
        }
    };

    const setSummary = (val) => setProfile(p => ({ ...p, summary: val }));

    const updateItem = (id, changes) => setProfile(p => ({
        ...p,
        history: p.history.map(h => h.id === id ? { ...h, ...changes } : h),
    }));

    const removeItem = (id) => setProfile(p => ({
        ...p,
        history: p.history.filter(h => h.id !== id),
    }));

    const addItem = () => setProfile(p => ({ ...p, history: [newHistoryItem(), ...p.history] }));

    const handleSave = async () => {
        setSaving(true);
        setErr('');
        setOk('');
        try {
            const sorted = sortHistory(profile.history);
            await updateProfile({ ...profile, history: sorted });
            setProfile(p => ({ ...p, history: sorted }));
            setOk('저장되었습니다.');
        } catch (e) {
            setErr(e.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading || !profile) return <div className="admin-content" style={{ textAlign: 'center', padding: 60 }}>불러오는 중...</div>;

    return (
        <div className="admin-content">
            <div className="admin-edit-header">
                <h2>프로필 관리</h2>
                <div className="admin-edit-header-btns">
                    <a href="/profile/main" target="_blank" rel="noreferrer" className="admin-btn-preview">미리보기</a>
                    <button className="admin-btn-save" onClick={handleSave} disabled={saving}>
                        {saving ? '저장 중...' : '저장'}
                    </button>
                </div>
            </div>

            {err && <div className="admin-error">{err}</div>}
            {ok && <div className="admin-ok">{ok}</div>}

            <div>
                <p className="admin-section-title">전체 요약 정보</p>
                <div className="admin-block">
                    <div className="admin-block-body">
                        <div className="admin-field">
                            <label>요약</label>
                            <textarea
                                value={profile.summary}
                                onChange={e => setSummary(e.target.value)}
                                placeholder="'Simulacre'를 주제로 꾸준한 작품활동을 하고 있으며..."
                                rows={5}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <p className="admin-section-title">연혁 (년도별 타이틀 / 상세 내역)</p>

                <button className="admin-btn-addimg" onClick={addItem} style={{ marginBottom: 10 }}>+ 연혁 항목 추가</button>

                {profile.history.map((item) => (
                    <div key={item.id} className="admin-block">
                        <div className="admin-block-header">
                            <span className="admin-block-badge">{item.year || '연도 미입력'}</span>
                            <div className="admin-block-controls">
                                <button className="admin-btn-del-block" onClick={() => removeItem(item.id)}>삭제</button>
                            </div>
                        </div>
                        <div className="admin-block-body">
                            <div className="admin-history-row">
                                <div className="admin-field admin-history-year">
                                    <label>년도</label>
                                    <input
                                        type="text"
                                        value={item.year}
                                        onChange={e => updateItem(item.id, { year: e.target.value })}
                                        placeholder="2024"
                                    />
                                </div>
                                <div className="admin-field admin-history-icon">
                                    <label>아이콘 (선택)</label>
                                    <div className="admin-history-icon-row">
                                        {item.icon && (
                                            <img src={process.env.PUBLIC_URL + item.icon} alt="preview" className="admin-history-icon-preview" />
                                        )}
                                        <input
                                            type="text"
                                            value={item.icon}
                                            onChange={e => updateItem(item.id, { icon: e.target.value })}
                                            placeholder="/assets/img/common/wa.png"
                                        />
                                        <label className="admin-history-icon-upload">
                                            <input type="file" accept="image/*" style={{ display: 'none' }}
                                                onChange={e => { const t = e.target; handleImageUpload(t.files[0], item.id, url => { updateItem(item.id, { icon: url }); t.value = ''; }); }}
                                            />
                                            <span className="admin-btn-upload">{uploadingIds[item.id] ? '업로드 중...' : '파일 선택'}</span>
                                        </label>
                                        {item.icon && (
                                            <button className="admin-btn-del-img" onClick={() => updateItem(item.id, { icon: '' })}>✕</button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="admin-field">
                                <label>타이틀</label>
                                <input
                                    type="text"
                                    value={item.title}
                                    onChange={e => updateItem(item.id, { title: e.target.value })}
                                    placeholder="Web Award Winner - Hyundai Steel, ESG Report 2022"
                                />
                            </div>
                            <div className="admin-field">
                                <label>상세 내역</label>
                                <input
                                    type="text"
                                    value={item.subtitle}
                                    onChange={e => updateItem(item.id, { subtitle: e.target.value })}
                                    placeholder="웹어워드 대상 - 현대제철 지속가능경영보고서 2022"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default AdminProfileEdit;
